import { useEffect, useState } from "react";
import { Megaphone, Plus, Trash2, AlertTriangle, Info, ShieldAlert, Save, Loader2, X } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Modal from "./Modal";
import VoiceTextarea from "./VoiceTextarea";

const SEVERITIES = [
  { v: "info", label: "Info", color: "#1FB6C1", icon: Info },
  { v: "warning", label: "Warning", color: "#F59E0B", icon: AlertTriangle },
  { v: "critical", label: "Critical", color: "#F43F5E", icon: ShieldAlert },
];

const AUDIENCES = [
  { v: "all", label: "Everyone" },
  { v: "role:owner", label: "Owners only" },
  { v: "role:admin", label: "Admins only" },
  { v: "role:manager", label: "Managers only" },
  { v: "role:rep", label: "Reps only" },
  { v: "role:technician", label: "Technicians only" },
];

const empty = { title: "", body: "", severity: "info", audience: "all", expires_at: "", pinned: false, push: false };

/**
 * BulletinManagerCard — owner / admin can create + delete app-wide
 * broadcast bulletins. Lives inside the Settings → Admin accordion.
 */
export default function BulletinManagerCard() {
  const { user } = useAuth();
  const allowed = user?.role === "owner" || user?.role === "admin";

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/bulletins");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load bulletins"));
    }
  };
  useEffect(() => {
    if (allowed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  if (!allowed) return null;

  const submit = async () => {
    if (!form.title.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post("/bulletins", {
        ...form,
        expires_at: form.expires_at || null,
      });
      setForm(empty);
      setOpen(false);
      await load();
      window.dispatchEvent(new CustomEvent("bulletins:refresh"));
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not create"));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bulletin?")) return;
    try {
      await api.delete(`/bulletins/${id}`);
      await load();
      window.dispatchEvent(new CustomEvent("bulletins:refresh"));
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not delete"));
    }
  };

  const sevMeta = (s) => SEVERITIES.find((x) => x.v === s) || SEVERITIES[0];

  return (
    <div className="hv-card p-5" data-testid="bulletin-manager-card">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F59E0B]/15 border border-[#F59E0B]/40 flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-[#F59E0B]" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-2xl font-bold">Bulletins</h2>
            <p className="text-xs text-[#a0aabf]">Broadcast a banner to staff. Each user can dismiss it independently.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="hv-btn hv-btn-primary text-xs"
          data-testid="bulletin-new-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New bulletin
        </button>
      </div>

      {err && (
        <div className="border border-rose-500/40 bg-rose-500/5 text-rose-300 text-xs p-2 rounded mb-3">{err}</div>
      )}

      {items.length === 0 ? (
        <div className="text-xs text-[#647087] italic text-center py-4" data-testid="bulletin-empty">
          No bulletins yet — click "New bulletin" to broadcast one.
        </div>
      ) : (
        <div className="space-y-2" data-testid="bulletin-list">
          {items.map((b) => {
            const meta = sevMeta(b.severity);
            const Icon = meta.icon;
            return (
              <div
                key={b.id}
                className="flex items-start gap-3 p-3 rounded-md border"
                style={{ borderColor: `${meta.color}40`, background: `${meta.color}08` }}
                data-testid={`bulletin-row-${b.id}`}
              >
                <span
                  className="w-7 h-7 shrink-0 rounded-md inline-flex items-center justify-center"
                  style={{ background: `${meta.color}26`, color: meta.color, border: `1px solid ${meta.color}55` }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{b.title}</div>
                  {b.body && <div className="text-xs text-[#a0aabf] mt-0.5 whitespace-pre-wrap line-clamp-2">{b.body}</div>}
                  <div className="flex items-center gap-2 flex-wrap mt-1.5 text-[10px] uppercase tracking-wider text-[#647087]">
                    <span>{meta.label}</span>
                    <span>·</span>
                    <span>{b.audience || "all"}</span>
                    {b.expires_at && <><span>·</span><span>expires {new Date(b.expires_at).toLocaleString("en-GB")}</span></>}
                    {b.dismiss_count > 0 && <><span>·</span><span>dismissed by {b.dismiss_count}</span></>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(b.id)}
                  className="hv-btn hv-btn-ghost text-xs shrink-0"
                  data-testid={`bulletin-delete-${b.id}`}
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5 text-rose-300" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New bulletin"
        testId="bulletin-modal"
        maxWidth="max-w-lg"
      >
        <div className="space-y-3" data-testid="bulletin-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Title *</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="hv-input mt-1"
              data-testid="bulletin-input-title"
              placeholder="Server maintenance Saturday 6pm"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Body</label>
            <VoiceTextarea
              value={form.body}
              onChange={(v) => setForm({...form, body: v})}
              data-testid="bulletin-input-body"
              placeholder="Optional details — kept short and skim-able."
             testIdPrefix="bulletin-input-body-voice" context="note" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Severity</label>
              <select
                value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value })}
                className="hv-input mt-1"
                data-testid="bulletin-input-severity"
              >
                {SEVERITIES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Audience</label>
              <select
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
                className="hv-input mt-1"
                data-testid="bulletin-input-audience"
              >
                {AUDIENCES.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Expires (optional)</label>
            <input
              type="datetime-local"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="hv-input mt-1"
              data-testid="bulletin-input-expires"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-[#a0aabf] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.push}
              onChange={(e) => setForm({ ...form, push: e.target.checked })}
              data-testid="bulletin-input-push"
            />
            Also fire a Web Push to every audience-matching user (closed-app alert)
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setOpen(false)} className="hv-btn hv-btn-ghost">Cancel</button>
            <button
              onClick={submit}
              disabled={busy || !form.title.trim()}
              className="hv-btn hv-btn-primary"
              data-testid="bulletin-submit"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Broadcast
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
